<template>
  <div class="gg-shell">
    <GloboGraphicsHeader />
    <GloboGraphicsToolbar />

    <div v-if="!cod_producao" class="gg-body gg-body--center">
      <div class="sc-empty-state gg-empty">
        <v-icon size="32" class="gg-empty__icon mb-3">mdi-television-off</v-icon>
        <span class="sc-empty-state__title gg-empty__title">Nenhum programa selecionado</span>
        <span class="sc-empty-state__subtitle gg-empty__subtitle">
          Selecione um programa para carregar as playlists, templates e cenas disponíveis.
        </span>
      </div>
    </div>
    <div v-else-if="!globographicsEnabled" class="gg-body gg-body--center">
      <div class="sc-empty-state gg-empty">
        <v-icon size="32" class="gg-empty__icon mb-3">mdi-palette-outline</v-icon>
        <span class="sc-empty-state__title gg-empty__title">Globo Graphics não está habilitado para este programa</span>
        <span class="sc-empty-state__subtitle gg-empty__subtitle">Habilite em Configuração de exibição → editar produção → Globo Graphics.</span>
      </div>
    </div>
    <div v-else-if="!siteReady" class="gg-body gg-body--center">
      <div class="sc-empty-state gg-empty">
        <v-progress-circular indeterminate color="primary" size="32" width="3" class="mb-3" />
        <span class="sc-empty-state__title gg-empty__title">Carregando site…</span>
      </div>
    </div>
    <div v-else class="gg-body gg-workspace">
      <!-- Left column: Playlists (top) + Templates e cenas (bottom). -->
      <div class="gg-workspace__col gg-workspace__col--left">
        <PlayoutPlaylistPanel
          v-if="cod_producao"
          :key="`playlist-${selectedSiteSigla}`"
          :cod_producao="cod_producao"
          :offline="vizrtOffline"
          class="gg-mod gg-mod--playlists"
        />
        <GloboGraphicsCenasDisponiveis
          :key="`cenas-${selectedSiteSigla}`"
          :cod_producao="cod_producao"
          :selected-id="previewCena ? previewCena.id_gh_cena : null"
          :offline="vizrtOffline"
          class="gg-mod gg-mod--templates"
          @select-scene="onSelectScene"
        />
      </div>

      <!-- Right column: ControlBar (top) + Preview + Editor. -->
      <div class="gg-workspace__col gg-workspace__col--right">
        <GloboGraphicsControlBar
          v-if="cod_producao"
          :state="controlState"
          :can-take="canTake"
          :can-navigate="canNavigate"
          @force-stop="onForceStop"
          @previous="onPrevious"
          @take="onTake"
          @next="onNext"
          @continue="onContinue"
          @takeout="onTakeout"
        />
        <GloboGraphicsCenaEmExibicao
          :key="`exibicao-${selectedSiteSigla}`"
          :cod_producao="cod_producao"
          :offline="vizrtOffline"
          class="gg-mod gg-mod--preview"
          :class="{ 'gg-mod--preview-active': previewHasContent }"
        />
        <GloboGraphicsInlineEditor
          :item="editorItem"
          :id-playlist="editorPlaylistId"
          :cod-producao="cod_producao"
          :offline="vizrtOffline"
          class="gg-mod gg-mod--editor"
        />
      </div>
    </div>

    <!-- Vizrt-offline: reconnect toast. The workspace keeps auto-probing health and
         recovers on its own; the operator can open a ticket if it persists. -->
    <transition name="gg-toast-fade">
      <GloboGraphicsToast
        v-if="vizrtOffline"
        variant="error"
        text="Sem conexão. Tentando reconectar automaticamente, se o problema persistir, abra um chamado."
        action-label="Abrir chamado"
        @action="openChamado"
      />
    </transition>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import GloboGraphicsHeader from "../components/GloboGraphics/GloboGraphicsHeader.vue";
import GloboGraphicsToolbar from "../components/GloboGraphics/GloboGraphicsToolbar.vue";
import GloboGraphicsCenasDisponiveis from "../components/GloboGraphics/GloboGraphicsCenasDisponiveis.vue";
import GloboGraphicsCenaEmExibicao from "../components/GloboGraphics/GloboGraphicsCenaEmExibicao.vue";
import PlayoutPlaylistPanel from "../components/GloboGraphics/PlayoutPlaylistPanel.vue";
import GloboGraphicsControlBar from "../components/GloboGraphics/GloboGraphicsControlBar.vue";
import GloboGraphicsInlineEditor from "../components/GloboGraphics/GloboGraphicsInlineEditor.vue";
import GloboGraphicsToast from "../components/GloboGraphics/GloboGraphicsToast.vue";
import { pickTakeTarget } from "../utils/playoutUtils";

// Re-probe Vizrt health on a loop so the workspace both notices a drop and
// recovers on its own once the site is reachable again.
const HEALTH_POLL_INTERVAL = 10000;
// Helpdesk/ticket target for the offline toast's "Abrir chamado". No endpoint is
// wired yet — set this to the team's chamado URL to enable the button.
const CHAMADO_URL = "";
// Browser-tab branding while the Globo Graphics workspace is open — its own title
// and favicon, restored to the app's defaults on leave.
const GG_TAB_TITLE = "Globo Graphics";
const GG_FAVICON = `${process.env.BASE_URL || "/"}globographics-favicon.svg`;

export default {
  name: "GloboGraphicsView",
  components: {
    GloboGraphicsHeader,
    GloboGraphicsToolbar,
    GloboGraphicsCenasDisponiveis,
    GloboGraphicsCenaEmExibicao,
    PlayoutPlaylistPanel,
    GloboGraphicsControlBar,
    GloboGraphicsInlineEditor,
    GloboGraphicsToast,
  },
  data() {
    return {
      cod_producao: null,
      // The Vizrt workflow is site-scoped, so it must not load until the production's
      // site is resolved — otherwise the template list fires against the previous
      // site and the late response leaves stale templates whose thumbnails 404.
      siteReady: false,
      timerHealth: null,
      checkingHealth: false,
    };
  },
  async created() {
    this.applyTabBranding();
    const selectedProducao = this.$store.state.producoes.selectedProducao;
    if (selectedProducao?.cod_producao) {
      this.cod_producao = selectedProducao.cod_producao;
      if (this.globographicsEnabled) await this.prepareSiteAndLoad();
    }
  },
  methods: {
    ...mapActions("sites", ["fetchSitesForProducao"]),
    ...mapActions({
      loadLinkedProjects: "globographics/loadLinkedProjects",
      loadSavedScenes: "globographics/loadSavedScenes",
      // Owned at the view level so child components (CenasDisponiveis,
      // PlayoutPlaylistPanel) read from the shared Vuex state instead of
      // each dispatching their own redundant probe.
      checkVizrtHealth: "globographics/checkVizrtHealth",
      // Playout transport — the ControlBar lives here now (relocated out of the
      // playlist panel), so the view dispatches take/nav/out/deactivate.
      playlistTakeItem: "playout/playlistTakeItem",
      playlistNext: "playout/playlistNext",
      playlistPrevious: "playout/playlistPrevious",
      outScene: "playout/outScene",
      continueScene: "playout/continueScene",
      deactivatePlaylist: "playout/deactivatePlaylist",
      selectPreviewCena: "playout/selectPreviewCena",
    }),
    // Resolve the production's site (sets the praça-default selection) BEFORE loading
    // the workflow, so templates/scenes only ever fetch once, with the correct site.
    async prepareSiteAndLoad() {
      // siteReady=false swaps the whole workspace out of the tree, which is what
      // discards the previous production's panel state.
      this.siteReady = false;
      await this.fetchSitesForProducao(this.cod_producao).catch(() => {});
      this.siteReady = true;
      await this.loadProductionData();
    },
    // NOTE: there is deliberately no clearWorkflow() here any more. It used to commit
    // a store slot that the template picker no longer reads, so it had quietly become
    // a no-op that still read as if it were doing the teardown. What actually re-scopes
    // the workflow is the `:key="...-${selectedSiteSigla}"` on the panels below: the
    // key change destroys and remounts them, discarding their component state and
    // making the self-fetching ones refetch. If you remove those keys, the previous
    // site's templates and thumbnails will render under the new site.
    async loadProductionData() {
      if (!this.cod_producao) return;
      try {
        await Promise.all([
          this.loadLinkedProjects({ codProducao: this.cod_producao }),
          this.loadSavedScenes({ codProducao: this.cod_producao }),
          this.checkVizrtHealth(),
        ]);
      } catch (_) {
        // If any resource fails the config may be missing, which will show the
        // "playout desativado" hint. Surface a toast so the operator doesn't
        // misread a fetch failure as a deliberate config state.
        this.$toast?.error?.('Erro ao carregar dados da produção');
      }
      this.updateTimers();
    },
    updateTimers() {
      clearInterval(this.timerHealth);
      this.checkingHealth = false;
      this.timerHealth = setInterval(async () => {
        if (this.cod_producao && !this.checkingHealth) {
          this.checkingHealth = true;
          try {
            await this.checkVizrtHealth();
          } finally {
            this.checkingHealth = false;
          }
        }
      }, HEALTH_POLL_INTERVAL);
    },

    // Select a saved cena from the Templates e cenas block → Preview + Editor.
    onSelectScene(scene) {
      this.selectPreviewCena(scene);
    },

    // Give the browser tab the Globo Graphics identity (title + favicon) while the
    // workspace is open; the originals are captured so leave() can restore them.
    applyTabBranding() {
      this.prevTabTitle = document.title;
      document.title = GG_TAB_TITLE;
      const link = document.querySelector("link[rel~='icon']");
      if (link) {
        this.faviconLink = link;
        this.prevFavicon = link.getAttribute("href");
        link.setAttribute("href", GG_FAVICON);
      }
    },
    restoreTabBranding() {
      if (this.prevTabTitle != null) document.title = this.prevTabTitle;
      if (this.faviconLink && this.prevFavicon != null) {
        this.faviconLink.setAttribute("href", this.prevFavicon);
      }
    },

    // Offline toast → open a support ticket. Kept behind a configured URL so the
    // button never opens a dead tab; surface a hint until the URL is set.
    openChamado() {
      if (CHAMADO_URL) {
        window.open(CHAMADO_URL, "_blank", "noopener");
      } else {
        this.$toast?.info?.("Abertura de chamado ainda não configurada.");
      }
    },

    // ── Playout transport (ControlBar) ──────────────────────────────────────
    // Operate on the active playlist; the ControlBar only enables these buttons
    // in states where an active playlist exists, so activePlaylist is set.
    // Take rolls the FIRST item of the playlist (re-taking the current one while
    // something is already on air). It is deliberately NOT preview-driven: what the
    // operator has selected in Preview has no bearing on the target, so previewing a
    // graphic can never put it on air by itself. Advance with Next, not Take.
    async onTake() {
      const target = this.takeTarget;
      if (!target) return;
      try {
        await this.playlistTakeItem({
          idPlaylist: this.activePlaylist.id_playlist,
          idItem: target.id_playlist_item,
          codProducao: this.cod_producao,
        });
      } catch (err) {
        this.$toast?.error?.(err.response?.data?.error || "Erro ao executar Take");
      }
    },
    async onNext() {
      if (!this.activePlaylist) return;
      try {
        await this.playlistNext({ idPlaylist: this.activePlaylist.id_playlist, codProducao: this.cod_producao });
      } catch (err) {
        this.$toast?.error?.(err.response?.data?.error || "Erro ao avançar");
      }
    },
    async onPrevious() {
      if (!this.activePlaylist) return;
      try {
        await this.playlistPrevious({ idPlaylist: this.activePlaylist.id_playlist, codProducao: this.cod_producao });
      } catch (err) {
        this.$toast?.error?.(err.response?.data?.error || "Erro ao voltar");
      }
    },
    async onContinue() {
      try {
        await this.continueScene(this.cod_producao);
      } catch (err) {
        this.$toast?.error?.(err.response?.data?.error || "Erro ao continuar");
      }
    },
    async onTakeout() {
      try {
        await this.outScene(this.cod_producao);
      } catch (err) {
        this.$toast?.error?.(err.response?.data?.error || "Erro ao remover do ar");
      }
    },
    // Forçar parar — deactivate the whole playout (harder than Takeout, which only
    // removes the on-air graphic while keeping the playlist active).
    async onForceStop() {
      if (!this.activePlaylist) return;
      try {
        await this.deactivatePlaylist({ idPlaylist: this.activePlaylist.id_playlist, codProducao: this.cod_producao });
      } catch (err) {
        this.$toast?.error?.(err.response?.data?.error || "Erro ao parar o playout");
      }
    },
  },
  computed: {
    ...mapGetters({
      vizrtHealth: "globographics/getterVizrtHealth",
      vizrtHealthChecked: "globographics/getterVizrtHealthChecked",
      activePlaylist: "playout/getterActivePlaylist",
      onAir: "playout/getterOnAir",
      previewItem: "playout/getterPreviewItem",
      previewItemPlaylistId: "playout/getterPreviewItemPlaylistId",
      previewCena: "playout/getterPreviewCena",
      onAirItem: "playout/getterOnAirItem",
      onAirItemPlaylistId: "playout/getterOnAirItemPlaylistId",
    }),
    ...mapGetters("sites", { selectedSiteSigla: "getterSelectedSiteSigla" }),
    // "Globo Graphics" (the Vizrt module) is per-production opt-in.
    globographicsEnabled() {
      return this.$store.state.producoes.selectedProducao?.settings?.globographics?.enabled || false;
    },
    mseHealthy() {
      return this.vizrtHealth?.mse?.healthy || false;
    },
    // The Preview module is a short placeholder when idle and grows to the full
    // render height once it has something to show (a selection, an active playlist's
    // program monitor, or an on-air graphic) — matching the design's two heights.
    previewHasContent() {
      return !!this.previewItem || !!this.previewCena || !!this.onAir || !!this.activePlaylist;
    },
    // "Sem conexão": the site's Vizrt is fully unreachable — every endpoint is down
    // (a total-disconnect probe, not a single degraded service, which the per-endpoint
    // status dots + ControlBar OFF already surface). Guarded on the first probe so the
    // all-false default never flashes the offline screen on mount.
    vizrtOffline() {
      if (!this.vizrtHealthChecked) return false;
      const h = this.vizrtHealth || {};
      return (
        !h.graphicHub?.healthy &&
        !h.pds?.healthy &&
        !h.preview?.healthy &&
        !h.mse?.healthy
      );
    },
    // With nothing selected the Editor follows whatever is ON AIR, so a live edit
    // lands on the graphic the engine is actually rendering. An explicit row
    // selection overrides that — the operator has to be able to prepare the next
    // template while another one is on air. Taking clears the selection, so the
    // Editor snaps back to following air.
    editorItem() {
      return this.previewItem || this.onAirItem;
    },
    editorPlaylistId() {
      return this.previewItem ? this.previewItemPlaylistId : this.onAirItemPlaylistId;
    },
    // What Take puts on air: the next item in line on the active playlist — the
    // on-air one if there is one, else the first published item. Take does NOT
    // follow the item selection: while a playlist is active the selection is an
    // editing cursor, and the engine output is the program.
    takeTarget() {
      return pickTakeTarget(this.activePlaylist);
    },
    // Status chip: what the playout is doing. PVW = a template is being previewed
    // (cued live on an engine) with nothing on air — now including the active-but-
    // not-yet-taken window, where the preview renders on the production's own engine.
    // On air → ON_AIR; nothing previewed and nothing on air → STBY.
    controlState() {
      if (!this.mseHealthy) return "OFF";
      if (this.onAir) return "ON_AIR";
      if (this.previewItem) return "PVW";
      return "STBY";
    },
    // Separate from the chip: whether there is a template to roll to air. On air
    // Take reads as "done", matching the design.
    canTake() {
      return !!this.takeTarget && !this.onAir && !this.vizrtOffline;
    },
    canNavigate() {
      return !!this.activePlaylist && !this.vizrtOffline;
    },
  },
  watch: {
    "$store.state.producoes.selectedProducao": async function (newValue) {
      if (!newValue || !newValue.cod_producao) {
        this.cod_producao = null;
        this.siteReady = false;
        clearInterval(this.timerHealth);
        this.timerHealth = null;
        return;
      }
      this.cod_producao = newValue.cod_producao;
      this.siteReady = false;
      if (this.globographicsEnabled) await this.prepareSiteAndLoad();
    },
    // Manual site change (the site is already resolved): re-scope the workflow. The
    // :key on the panels is what drops the stale site's data — changing the sigla
    // remounts the self-fetching ones (templates/browse/playlist) so they refetch
    // for the new site. This only has to reload what the view itself owns.
    selectedSiteSigla() {
      if (!this.cod_producao || !this.siteReady) return;
      this.loadProductionData();
    },
  },
  beforeDestroy() {
    clearInterval(this.timerHealth);
    this.restoreTabBranding();
  },
};
</script>

<style lang="scss" scoped>
// Inter is the Globo Graphics UI font. Loaded here (not in index.html) so it
// only downloads inside this module; applied via .gg-shell below. The
// "Globo Graphics" wordmark keeps Globotipo — see the header.
@import url("https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap");

.gg-shell {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: var(--sc-gg-bg);
  font-family: "Inter", "Globotipo", sans-serif;
}

.gg-body {
  flex: 1 1 auto;
}

.gg-body--center {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

// Two columns, sized to the design's 789 : 1056 ratio (flex-grow weights) with a
// 24px gutter and 24px page padding. Each column is a vertical stack of modules;
// modules carry their own (resizable) height and scroll internally — a huge
// playlist scrolls inside its module, and dragging a grip grows that module + the
// whole page (which scrolls) rather than shrinking its neighbours.
.gg-workspace {
  display: flex;
  align-items: flex-start;
  gap: var(--sc-space-6);
  padding: var(--sc-space-6);
}

.gg-workspace__col {
  display: flex;
  flex-direction: column;
  gap: var(--sc-space-6);
  min-width: 0;
}

.gg-workspace__col > * {
  min-height: 0;
}

.gg-workspace__col--left {
  flex: 789 1 0;
}

.gg-workspace__col--right {
  flex: 1056 1 0;
}

// Per-module default heights, from the design's idle layout. The Preview grows to
// its full render height only once it has content (else it would show a large
// empty box); an operator can still drag any module's grip to resize.
.gg-mod--playlists {
  height: 411px;
}

.gg-mod--templates {
  height: 410px;
}

.gg-mod--editor {
  height: 200px;
}

.gg-mod--preview {
  height: 200px;
}

.gg-mod--preview-active {
  height: 546px;
}

/* GG empty state — larger, all-white type per the design spec. Reuses the
   .sc-empty-state primitive for structure; the modifier only adjusts the
   module-specific sizing/colour. */
.gg-empty {
  max-width: 360px;
}

.gg-empty__icon {
  color: var(--sc-gg-text-primary) !important;
}

.gg-empty__title {
  font-size: 20px;
  line-height: 24px;
  color: var(--sc-gg-text-primary);
}

.gg-empty__subtitle {
  max-width: 340px;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: -0.08px;
  color: var(--sc-gg-text-primary);
}

</style>
